import React from "react";
import { HStack, Heading } from "@chakra-ui/react";
import { Switch, FormControl, FormLabel } from "@chakra-ui/react";

import {
  MODULE_LOCATIONS_LIST,
  ModuleWithUpNext,
  PossibleLocations,
  PossibleLocationsOrAll,
  StepsAny,
} from "@constants";
import { ModulesUpNext, LocationSelect, Loading } from "@components";
import {
  useAuth,
  useGetRecentModules,
  useQueryParam,
  useStoreModuleStatus,
} from "@hooks";
import { Layout } from "./PageLayouts";

/**
 * A page to display all the modules and the next production step that needs
 * to be performed on them.
 */
export function UpNextPage() {
  const { user } = useAuth();
  const [location, setLocation] = useQueryParam<PossibleLocationsOrAll>(
    "location",
    user ? user.location : "All",
  );
  const [useBadModules, setUseBadModules] = React.useState(false);

  const handleToggle = () => {
    setUseBadModules((prev) => !prev);
  };

  return (
    <Layout>
      <HStack marginTop="16px" marginBottom="16px">
        <Heading flex="1">Up Next</Heading>
        <FormControl display="flex" alignItems="center" width="auto">
          <FormLabel htmlFor="toggle-bad-modules" mb="0">
            Include Bad Modules
          </FormLabel>
          <Switch
            id="toggle-bad-modules"
            isChecked={useBadModules}
            onChange={handleToggle}
          />
        </FormControl>
        <LocationSelect
          maxWidth={200 / 16 + "rem"}
          location={location}
          setLocation={setLocation}
        />
      </HStack>
      <InnerWrapper location={location} useBadModules={useBadModules} />
    </Layout>
  );
}

// The Inner Wrapper is required to prevent multiple rerenders as the location prop is passed to the query hooks.
function InnerWrapper({
  location,
  useBadModules,
}: {
  location: PossibleLocationsOrAll;
  useBadModules: boolean;
}) {
  const adjustedLocation = React.useMemo(
    () => (location === "All" ? undefined : location),
    [location],
  );

  // The fix: Pass the useBadModules state to the hook.
  const { documents: recentModules } = useGetRecentModules(
    adjustedLocation,
    useBadModules,
  );

  const filteredModulesUpNext = useStoreAsModulesUpNext(
    adjustedLocation,
    useBadModules,
  );
  console.log({ filteredModulesUpNext });

  if (recentModules === undefined || filteredModulesUpNext === undefined)
    return <Loading />;
  return (
    <ModulesUpNext
      location={adjustedLocation}
      recentModules={recentModules}
      modulesUpNext={filteredModulesUpNext}
    />
  );
}

function useStoreAsModulesUpNext(
  userLocation: PossibleLocations | undefined,
  useBadModules: boolean,
) {
  const store = useStoreModuleStatus();
  return React.useMemo(() => {
    if (store === undefined) return undefined;
    // Transform store into list of { serial: string; nextStep: StepsAny; }
    const moduleIdsWithNextStep = (
      Object.entries(store.modulesForStep) as [StepsAny, string[]][]
    )
      .map(([nextStep, serials]) =>
        serials.map((serial) => ({ serial, nextStep })),
      )
      .reduce((fullList, stepList) => fullList.concat(stepList), []);
    // Go back to the Store and add locations to each of these modules.
    const moduleIdsWithNextStepAndLocation = moduleIdsWithNextStep
      .map(({ serial, nextStep }) => {
        let location: PossibleLocations | undefined = undefined;
        let moduleStatus = "production";
        for (const loc of MODULE_LOCATIONS_LIST) {
          if ((store.partsByLocation[loc] ?? []).includes(serial)) {
            location = loc;
          }
        }
        if ((store.badModules ?? []).includes(serial)) {
          moduleStatus = "do_not_use";
        }
        return { serial, nextStep, location, moduleStatus };
      })
      // Because there's a chance that the location could be undefined, filter the undefined values
      .filter(({ location }) => location !== undefined)
      .filter(
        ({ moduleStatus }) => moduleStatus != "do_not_use" || useBadModules,
      );
    // Format objects as ModuleWithUpNext
    const modulesUpNext = moduleIdsWithNextStepAndLocation.map(
      ({ serial, location, nextStep }) =>
        ({
          id: serial,
          location: { location, inTransit: false },
          nextStep,
        }) as ModuleWithUpNext,
    );

    if (userLocation === undefined) return modulesUpNext;
    return modulesUpNext.filter(
      ({ location }) => location.location === userLocation,
    );
  }, [userLocation, useBadModules, store]);
}
